export interface IScormInitialState {
  lessonLocation?: string | null
  suspendData?: string | null
  completed?: boolean
}

export interface IScormProgress {
  completed: boolean
  percent: number | null // 0-100, or null when the package reports no measurable progress
  lessonLocation: string
  suspendData: string
  started: boolean
  sessionSeconds: number // time spent in THIS session (from cmi.core.session_time / cmi.session_time)
  cmi: Record<string, string> // full SCORM data model the package reported (score, interactions/answers, etc.)
}

const parseCmiTimespan = (value: string): number => {
  // SCORM 1.2 CMITimespan: HHHH:MM:SS.ss
  const m = value.match(/^(\d+):(\d{2}):(\d{2})(\.\d+)?$/)
  if (!m) return 0
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) + (m[4] ? Number(m[4]) : 0)
}

const parseIso8601Duration = (value: string): number => {
  // SCORM 2004 duration: PT#H#M#S
  const m = value.match(/^P(?:T(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?)?$/)
  if (!m) return 0
  return Number(m[1] ?? 0) * 3600 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0)
}

// Restores a SCORM package to where the user left off. The two mechanisms the spec defines:
//  - cmi.core.entry / cmi.entry = "resume"  → tells the package "this is a continuation, not a fresh start"
//  - cmi.core.lesson_location / cmi.location + cmi.suspend_data  → the actual bookmark/state to restore
// Without returning these on init, every package always starts from the beginning.
export const installScormApiShim = (
  initialState: IScormInitialState,
  onProgress: (progress: IScormProgress) => void
): (() => void) => {
  const hasBookmark = !!(initialState.lessonLocation || initialState.suspendData)

  const cmi: Record<string, string> = {}
  if (initialState.lessonLocation) {
    cmi['cmi.core.lesson_location'] = initialState.lessonLocation
    cmi['cmi.location'] = initialState.lessonLocation
  }
  if (initialState.suspendData) {
    cmi['cmi.suspend_data'] = initialState.suspendData
  }
  if (initialState.completed) {
    cmi['cmi.core.lesson_status'] = 'completed'
    cmi['cmi.completion_status'] = 'completed'
  }

  let completed = !!initialState.completed
  let started = false
  let percent: number | null = null
  let scoreRaw: number | null = null
  let scoreMax = 100
  let sessionSeconds = 0

  const recomputePercent = () => {
    if (scoreRaw !== null) percent = Math.min(100, Math.max(0, (scoreRaw / (scoreMax || 100)) * 100))
  }

  const report = () =>
    onProgress({
      completed,
      percent: completed ? 100 : percent,
      lessonLocation: cmi['cmi.core.lesson_location'] || cmi['cmi.location'] || '',
      suspendData: cmi['cmi.suspend_data'] || '',
      started,
      sessionSeconds,
      cmi: { ...cmi },
    })

  const setValue = (name: string, value: string): string => {
    // SCORM values are strings per spec, but some packages pass numbers (e.g. lesson_location = 4).
    // Coerce so downstream JSON always sends strings and the backend doesn't reject them.
    value = value === null || value === undefined ? '' : String(value)
    cmi[name] = value
    started = true

    if (name === 'cmi.core.lesson_status' && (value === 'completed' || value === 'passed')) completed = true
    if (name === 'cmi.completion_status' && value === 'completed') completed = true
    if (name === 'cmi.success_status' && value === 'passed') completed = true

    // keep both spec variants of the bookmark in sync
    if (name === 'cmi.core.lesson_location') cmi['cmi.location'] = value
    if (name === 'cmi.location') cmi['cmi.core.lesson_location'] = value

    if (name === 'cmi.progress_measure') {
      const parsed = parseFloat(value)
      if (!isNaN(parsed)) percent = Math.min(100, Math.max(0, parsed * 100))
    }
    if (name === 'cmi.score.scaled') {
      const parsed = parseFloat(value)
      if (!isNaN(parsed)) percent = Math.min(100, Math.max(0, parsed * 100))
    }
    if (name === 'cmi.core.score.raw' || name === 'cmi.score.raw') {
      const parsed = parseFloat(value)
      if (!isNaN(parsed)) {
        scoreRaw = parsed
        recomputePercent()
      }
    }
    if (name === 'cmi.core.score.max' || name === 'cmi.score.max') {
      const parsed = parseFloat(value)
      if (!isNaN(parsed) && parsed > 0) {
        scoreMax = parsed
        recomputePercent()
      }
    }

    if (name === 'cmi.core.session_time') sessionSeconds = parseCmiTimespan(value)
    if (name === 'cmi.session_time') sessionSeconds = parseIso8601Duration(value)

    return 'true'
  }

  const getValue = (name: string): string => {
    if (name === 'cmi.core.entry' || name === 'cmi.entry') return hasBookmark ? 'resume' : 'ab-initio'
    if (cmi[name] !== undefined) return cmi[name]
    if (name === 'cmi.core.lesson_status') return hasBookmark && !completed ? 'incomplete' : 'not attempted'
    if (name === 'cmi.completion_status') return hasBookmark && !completed ? 'incomplete' : 'unknown'
    if (name === 'cmi.success_status') return 'unknown'
    return ''
  }

  const api12 = {
    LMSInitialize: () => 'true',
    LMSFinish: () => {
      report()
      return 'true'
    },
    LMSGetValue: getValue,
    LMSSetValue: setValue,
    LMSCommit: () => {
      report()
      return 'true'
    },
    LMSGetLastError: () => '0',
    LMSGetErrorString: () => '',
    LMSGetDiagnostic: () => '',
  }

  const api2004 = {
    Initialize: () => 'true',
    Terminate: () => {
      report()
      return 'true'
    },
    GetValue: getValue,
    SetValue: setValue,
    Commit: () => {
      report()
      return 'true'
    },
    GetLastError: () => '0',
    GetErrorString: () => '',
    GetDiagnostic: () => '',
  }

  ;(window as any).API = api12
  ;(window as any).API_1484_11 = api2004

  return () => {
    report()
    delete (window as any).API
    delete (window as any).API_1484_11
  }
}
